import { PRODUCTION_AUTO_SCHEDULING_ROUTES } from '../../productionAutoSchedulingRoutes';

export type NavIconKey =
  | 'apartment'
  | 'areaChart'
  | 'clock'
  | 'dashboard'
  | 'experiment'
  | 'key'
  | 'project'
  | 'rocket'
  | 'safety'
  | 'schedule'
  | 'setting'
  | 'table'
  | 'team'
  | 'userSwitch';

export type NavLeaf = {
  key: string;
  label: string;
  path: string;
  icon: NavIconKey;
  /** 需要的权限码（permission_code）；不传则所有人可见。 */
  requiredPermission?: string;
  /** 平台控制面权限码；与 Site 权限分开判断。 */
  requiredPlatformPermission?: string;
};

export type NavGroup = {
  key: string;
  label: string;
  subtitle: string;
  path?: string;
  icon: NavIconKey;
  children?: NavLeaf[];
  /** 顶层入口需要的权限码（无 children 的组用）。 */
  requiredPermission?: string;
};

const PLATFORM_CONTROL_PLANE_PATHS = new Set([
  '/governance/sites',
  '/governance/identities',
  '/governance/holiday-calendar',
]);

export const isPlatformControlPlanePath = (path: string): boolean => (
  PLATFORM_CONTROL_PLANE_PATHS.has(path)
);

export const canSeeNavigationItem = (
  requiredPermission: string | undefined,
  hasAuthenticatedUser: boolean,
  authEnforced: boolean,
  hasPermission: (permission: string) => boolean,
  requiredPlatformPermission?: string,
  hasPlatformPermission: (permission: string) => boolean = () => false,
): boolean => {
  if (!requiredPermission && !requiredPlatformPermission) return true;
  if (!hasAuthenticatedUser && !authEnforced) return true;
  if (requiredPlatformPermission) {
    return hasPlatformPermission(requiredPlatformPermission);
  }
  return requiredPermission ? hasPermission(requiredPermission) : false;
};

export const productionAutoSchedulingNavGroup: NavGroup = {
  key: 'production-auto-scheduling',
  label: '自动排产',
  subtitle: 'Auto Scheduling',
  icon: 'experiment',
  children: [
    { key: 'production-auto-cip-topology', icon: 'project', label: '资源与物理拓扑', path: PRODUCTION_AUTO_SCHEDULING_ROUTES.cipTopology, requiredPermission: 'MASTER_RESOURCE_READ' },
    { key: 'production-auto-cip-loops', icon: 'project', label: 'CIP 逻辑回路', path: PRODUCTION_AUTO_SCHEDULING_ROUTES.cipLoops, requiredPermission: 'MASTER_RESOURCE_READ' },
    { key: 'production-auto-equipment-state-model', icon: 'project', label: '设备类别与状态机', path: PRODUCTION_AUTO_SCHEDULING_ROUTES.equipmentStateModel, requiredPermission: 'MASTER_RESOURCE_READ' },
    { key: 'production-auto-facility-master', icon: 'project', label: '设施主数据', path: PRODUCTION_AUTO_SCHEDULING_ROUTES.facilityMaster, requiredPermission: 'MASTER_RESOURCE_READ' },
    { key: 'production-auto-process-templates', icon: 'project', label: '排产工艺模板', path: PRODUCTION_AUTO_SCHEDULING_ROUTES.processTemplates, requiredPermission: 'APS_TEMPLATE_READ' },
    { key: 'production-auto-process-template-contract', icon: 'project', label: '模板需求与时序', path: PRODUCTION_AUTO_SCHEDULING_ROUTES.processTemplateContract, requiredPermission: 'APS_TEMPLATE_READ' },
    { key: 'production-auto-world-state', icon: 'project', label: '世界状态与执行', path: PRODUCTION_AUTO_SCHEDULING_ROUTES.worldState, requiredPermission: 'MASTER_RESOURCE_READ' },
  ],
};

export const resolveProductionAutoSchedulingRootTarget = (
  canSee: (requiredPermission?: string) => boolean,
): string => (
  productionAutoSchedulingNavGroup.children?.find((child) => canSee(child.requiredPermission))?.path
  ?? '/operations-overview'
);

export const navGroups: NavGroup[] = [
  {
    key: 'operations-overview',
    label: '运营总览',
    subtitle: 'Ops Overview',
    path: '/operations-overview',
    icon: 'areaChart',
    requiredPermission: 'SYSTEM_DASHBOARD_READ',
  },
  {
    key: 'my-schedule',
    label: '我的排班',
    subtitle: 'My Shifts',
    path: '/my-schedule',
    icon: 'clock',
    requiredPermission: 'ROSTER_SCHEDULE_READ',
  },
  {
    key: 'base-data',
    label: '基础数据',
    subtitle: 'Master Data',
    icon: 'setting',
    children: [
      { key: 'equipment-management', icon: 'setting', label: '资源节点管理', path: '/equipment-management', requiredPermission: 'MASTER_RESOURCE_READ' },
      { key: 'qualifications', icon: 'safety', label: '资质管理', path: '/qualifications', requiredPermission: 'MASTER_QUALIFICATION_READ' },
      { key: 'qualification-matrix', icon: 'table', label: '资质矩阵', path: '/qualification-matrix', requiredPermission: 'MASTER_QUALIFICATION_READ' },
      { key: 'operations', icon: 'setting', label: '操作管理', path: '/operations', requiredPermission: 'MASTER_OPERATION_READ' },
      { key: 'operation-types', icon: 'project', label: '操作类型', path: '/operation-types', requiredPermission: 'MASTER_OPERATION_READ' },
    ],
  },
  {
    key: 'production',
    label: '生产计划',
    subtitle: 'Operations',
    icon: 'project',
    children: [
      { key: 'process-templates', icon: 'project', label: '工艺模版', path: '/process-templates', requiredPermission: 'APS_TEMPLATE_READ' },
      { key: 'batch-management-v4', icon: 'project', label: '批次管理 V4', path: '/batch-management-v4', requiredPermission: 'APS_BATCH_READ' },
      { key: 'batch-management-workbench-v2', icon: 'project', label: '批次管理工作台 V2', path: '/batch-management-workbench-v2', requiredPermission: 'APS_BATCH_READ' },
    ],
  },
  productionAutoSchedulingNavGroup,
  {
    key: 'personnel',
    label: '人员与排班',
    subtitle: 'Workforce',
    icon: 'apartment',
    children: [
      { key: 'organization-workbench', icon: 'apartment', label: '组织与人员', path: '/organization-workbench', requiredPermission: 'MASTER_EMPLOYEE_READ' },
      { key: 'personnel-scheduling', icon: 'clock', label: '人员排班', path: '/personnel-scheduling', requiredPermission: 'ROSTER_SCHEDULE_READ' },
      { key: 'roster-triage', icon: 'team', label: '排班分诊台', path: '/roster/triage', requiredPermission: 'ROSTER_SCHEDULE_READ' },
      { key: 'roster-leadership-cockpit', icon: 'dashboard', label: '工厂人力韧性驾驶舱', path: '/roster/leadership-cockpit', requiredPermission: 'ROSTER_COCKPIT_READ' },
      { key: 'solver-v4', icon: 'rocket', label: 'V4 自动排班', path: '/solver-v4', requiredPermission: 'SOLVER_RUN_READ' },
      { key: 'solver-v5', icon: 'experiment', label: 'V5 自动排班（增强可视化）', path: '/solver-v5', requiredPermission: 'SOLVER_RUN_READ' },
      { key: 'shift-definitions', icon: 'schedule', label: '班次定义', path: '/shift-definitions', requiredPermission: 'MASTER_SHIFT_DEF_READ' },
    ],
  },
  {
    key: 'governance',
    label: '权限治理',
    subtitle: 'Governance',
    icon: 'safety',
    children: [
      { key: 'governance-sites', icon: 'apartment', label: 'Site 管理', path: '/governance/sites', requiredPlatformPermission: 'PLATFORM_SITE_READ' },
      { key: 'governance-holiday-calendar', icon: 'schedule', label: '节假日日历', path: '/governance/holiday-calendar', requiredPlatformPermission: 'PLATFORM_SITE_CONFIGURE' },
      { key: 'governance-identities', icon: 'team', label: '平台用户', path: '/governance/identities', requiredPlatformPermission: 'PLATFORM_IDENTITY_READ' },
      { key: 'governance-roles', icon: 'key', label: '角色管理', path: '/governance/roles', requiredPermission: 'GOVERNANCE_ROLE_READ' },
      { key: 'governance-users', icon: 'userSwitch', label: '用户授权', path: '/governance/users', requiredPermission: 'GOVERNANCE_USER_READ' },
      { key: 'governance-permissions', icon: 'team', label: '权限目录', path: '/governance/permissions', requiredPermission: 'GOVERNANCE_ROLE_READ' },
    ],
  },
  {
    key: 'ui-kit',
    label: 'UI 组件库',
    subtitle: 'Design System',
    icon: 'rocket',
    children: [
      { key: 'ui-kit-home', icon: 'rocket', label: 'wxb-ui 组件库', path: '/ui-kit' },
      { key: 'process-flow-builder', icon: 'experiment', label: '排产原型 · 主工艺构建', path: '/process-flow-builder', requiredPermission: 'APS_TEMPLATE_READ' },
      { key: 'production-scheduling', icon: 'experiment', label: '排产原型 · 排产结果', path: '/production-scheduling', requiredPermission: 'APS_TEMPLATE_READ' },
      { key: 'process-flow-templates', icon: 'experiment', label: '排产原型 · 工艺模板列表', path: '/process-flow-templates', requiredPermission: 'APS_TEMPLATE_READ' },
      { key: 'derivable-library', icon: 'experiment', label: '排产原型 · 派生库', path: '/derivable-library', requiredPermission: 'APS_TEMPLATE_READ' },
      { key: 'ps-resource-master', icon: 'experiment', label: '排产原型 · 资源主数据', path: '/ps-resource-master', requiredPermission: 'APS_TEMPLATE_READ' },
      { key: 'factory-sandtable', icon: 'experiment', label: '排产原型 · 工厂沙盘', path: '/factory-sandtable', requiredPermission: 'APS_TEMPLATE_READ' },
    ],
  },
];

const matchesPath = (pathname: string, path: string) => (
  pathname === path || pathname.startsWith(`${path}/`)
);

export const getGroupSelection = (pathname: string, groups: NavGroup[]) => {
  for (const group of groups) {
    if (group.path && matchesPath(pathname, group.path)) {
      return { group, child: undefined };
    }

    const child = group.children?.find((item) => matchesPath(pathname, item.path));
    if (child) {
      return { group, child };
    }
  }

  return { group: groups[0], child: undefined };
};
